"use client";

/**
 * Navigator 글 복사 차단 (2026-10-12 대표 결정) — `[data-nocopy]` 영역 (상세 제목·본문·퀴즈 카드 · 홈 게시판·/note 목록 줄) 에서
 * 드래그 선택 · 복사/잘라내기 (클립보드에 빈 값) · 오른쪽 클릭 메뉴 · 이미지 끌기를 막는다. 길게 누르기 선택·돋보기·복사 메뉴는
 * CSS (`globals.css` `.note-nocopy` · user-select·-webkit-touch-callout) + contextmenu 차단으로 막는다.
 * 렌더 0 · 문서 단위 리스너 1벌 (여러 곳에 붙어도 한 번만 등록) · `[data-nocopy]` 밖·입력칸은 그대로.
 * 클릭 (퀴즈 보기·정답 보기·링크) 은 막지 않는다 · 본문은 서버 렌더 HTML 에 그대로 (SEO·스크린리더 영향 0) · 화면 캡처는 막을 수 없다.
 * 부착: NoteShell (/note 목록·상세) · NoteHomeBoard (홈).
 */
import { useEffect } from "react";

const SEL = "[data-nocopy]";
let mounted = 0;

function guardOf(n: EventTarget | Node | null): Element | null {
  const el = n instanceof Element ? n : n instanceof Node ? n.parentElement : null;
  return el ? el.closest(SEL) : null;
}

function isEditable(n: EventTarget | null): boolean {
  return n instanceof HTMLElement && (n.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(n.tagName));
}

/** 선택 범위가 차단 영역에 걸치는가 (전체 선택 포함). */
function selectionTouchesGuard(): boolean {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel || sel.rangeCount === 0) return false;
  const areas = Array.from(document.querySelectorAll(SEL));
  for (let i = 0; i < sel.rangeCount; i++) {
    const r = sel.getRangeAt(i);
    if (guardOf(r.commonAncestorContainer)) return true;
    if (!r.collapsed && areas.some((a) => r.intersectsNode(a))) return true;
  }
  return false;
}

function selectionIsEmpty(): boolean {
  const sel = window.getSelection();
  return !sel || sel.isCollapsed || sel.toString() === "";
}

function onCopyCut(e: ClipboardEvent) {
  if (isEditable(e.target) || isEditable(document.activeElement)) return;
  // 차단 영역 안 · 선택이 차단 영역에 걸침 · 선택이 비어 있음 (차단 영역 선택이 지워진 뒤 복사) → 빈 값
  if (!guardOf(e.target) && !selectionTouchesGuard() && !selectionIsEmpty()) return;
  e.preventDefault();
  // 클립보드를 빈 값으로 (이전 내용도 남기지 않음)
  e.clipboardData?.setData("text/plain", "");
  e.clipboardData?.setData("text/html", "");
}

function onBlock(e: Event) {
  if (isEditable(e.target)) return;
  if (guardOf(e.target)) e.preventDefault();
}

/** 전체 선택 (Ctrl/Cmd+A · 메뉴) 등으로 선택이 차단 영역에 걸치면 선택을 지운다 — Chrome 은 user-select:none 글도 전체 선택에 넣는다. */
function onSelectionChange() {
  if (isEditable(document.activeElement)) return;
  const sel = window.getSelection();
  if (sel && !sel.isCollapsed && selectionTouchesGuard()) sel.removeAllRanges();
}

/** 끌기를 막으면 링크 위 드래그가 클릭으로 바뀌어 이동한다 — 누른 뒤 6px 넘게 움직였으면 그 클릭은 무시 (원래 링크 끌기처럼). */
let down: { x: number; y: number } | null = null;
function onMouseDown(e: MouseEvent) {
  down = e.button === 0 && guardOf(e.target) ? { x: e.clientX, y: e.clientY } : null;
}
function onClick(e: MouseEvent) {
  const d = down;
  down = null;
  if (d && guardOf(e.target) && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6) {
    e.preventDefault();
    e.stopPropagation();
  }
}

function install() {
  document.addEventListener("selectionchange", onSelectionChange);
  document.addEventListener("mousedown", onMouseDown, true);
  document.addEventListener("click", onClick, true);
  document.addEventListener("copy", onCopyCut, true);
  document.addEventListener("cut", onCopyCut, true);
  document.addEventListener("contextmenu", onBlock, true);
  document.addEventListener("dragstart", onBlock, true);
  document.addEventListener("selectstart", onBlock, true);
}

function uninstall() {
  document.removeEventListener("selectionchange", onSelectionChange);
  document.removeEventListener("mousedown", onMouseDown, true);
  document.removeEventListener("click", onClick, true);
  document.removeEventListener("copy", onCopyCut, true);
  document.removeEventListener("cut", onCopyCut, true);
  document.removeEventListener("contextmenu", onBlock, true);
  document.removeEventListener("dragstart", onBlock, true);
  document.removeEventListener("selectstart", onBlock, true);
}

export default function NoteCopyGuard() {
  useEffect(() => {
    if (mounted++ === 0) install();
    return () => {
      if (--mounted === 0) uninstall();
    };
  }, []);
  return null;
}
